import { useEffect, useRef } from "react";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useThemeStore } from "../state/theme";
import styles from "./MatrixRain.module.css";

interface MatrixRainProps {
  /** Max alpha 0..1 for character tint. */
  opacity?: number;
  /** Overlay z-index mode — "bg" sits behind content, "fg" over it. */
  layer?: "bg" | "fg";
}

const CHAR_SET =
  "01アイウエオカキクケコサシスセソタチツテトナニヌネノ{}[]#·◈◇≡≋";

export function MatrixRain({ opacity = 0.07, layer = "bg" }: MatrixRainProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const reduced = useReducedMotion();
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    if (reduced || theme === "light") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const { innerWidth: w, innerHeight: h } = window;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.scale(dpr, dpr);
    };
    resize();

    const fontSize = 14;
    const columns = Math.floor(window.innerWidth / fontSize);
    const drops = new Array(columns).fill(1);

    let last = 0;
    const tick = (now: number) => {
      if (now - last < 60) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      last = now;
      ctx.fillStyle = `rgba(10,10,10,0.12)`;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.font = `${fontSize}px "JetBrains Mono", monospace`;
      ctx.fillStyle = `rgba(0, 255, 65, ${opacity})`;

      for (let i = 0; i < drops.length; i++) {
        const ch = CHAR_SET[Math.floor(Math.random() * CHAR_SET.length)];
        ctx.fillText(ch, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > window.innerHeight && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    window.addEventListener("resize", resize);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [reduced, theme, opacity]);

  if (reduced || theme === "light") return null;

  return (
    <canvas
      ref={canvasRef}
      className={`${styles.canvas} ${layer === "fg" ? styles.fg : styles.bg}`}
      aria-hidden
    />
  );
}
